/**
 * Guide engine v3 - ring palette (#1023, epic #1020). Spec R1.3 / R14.6.
 *
 * Ring colors come from a fixed-hue palette (blue, green, purple, yellow,
 * orange, off-white). Hue variations (teal, magenta, etc.) are fine. RED and
 * red-dominant hues are a hard ban: red reads as an error / destructive cue and
 * must never ring a guide target. White must be off-white, never pure `#ffffff`
 * (project DESIGN_SYSTEM.md section 2.8). Within a step every ring hex is unique.
 *
 * The validator uses isRedHue() + isPureWhite() for the R14.6 check; it does NOT
 * pin colors to the exact ramp (hue variations are allowed). The ramp below is
 * the default generation order an author may rely on or override.
 */

/** Default ring hex ramp, keyed by palette name (R1.3, overridable per highlight). */
export const RING_RAMP = {
  blue: '#3B82F6',
  green: '#0BA873',
  purple: '#8B5CF6',
  yellow: '#FACC15',
  orange: '#F59E0B',
  white: '#FBFBFC',
};

/** Default ring generation order when colors are not explicitly set (R1.3). */
export const RING_ORDER = [
  RING_RAMP.blue,
  RING_RAMP.green,
  RING_RAMP.purple,
  RING_RAMP.yellow,
  RING_RAMP.orange,
  RING_RAMP.white,
];

/**
 * Parse a `#rgb` or `#rrggbb` hex into 0-255 channels.
 * @param {string} hex
 * @returns {{r:number, g:number, b:number}|null} null if not a valid hex color.
 */
export const parseHex = (hex) => {
  if (typeof hex !== 'string') return null;
  const m = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

/**
 * Hue in degrees (0-360) for an rgb triple; 0 for achromatic (grey/white/black).
 * @param {{r:number, g:number, b:number}} rgb
 * @returns {{hue:number, sat:number}} sat is 0-1 chroma proxy (max-min)/255.
 */
const hueOf = ({ r, g, b }) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  if (delta === 0) return { hue: 0, sat: 0 };
  let hue;
  if (max === rn) hue = ((gn - bn) / delta) % 6;
  else if (max === gn) hue = (bn - rn) / delta + 2;
  else hue = (rn - gn) / delta + 4;
  hue *= 60;
  if (hue < 0) hue += 360;
  return { hue, sat: delta };
};

/**
 * True if a color is red or red-dominant (banned ring hue, R1.3 / R14.6).
 * The red wedge is hue < 20 or hue > 340. Near-greys (low chroma) are not red.
 * An unparseable color is treated as banned so a malformed hex fails the check.
 * @param {string} hex
 * @returns {boolean}
 */
export const isRedHue = (hex) => {
  const rgb = parseHex(hex);
  if (!rgb) return true;
  const { hue, sat } = hueOf(rgb);
  if (sat < 0.12) return false; // grey / off-white / near-mono: not a red hue
  return hue < 20 || hue > 340;
};

/**
 * True if a color is pure white `#ffffff` / `#fff` (banned, project section 2.8).
 * @param {string} hex
 * @returns {boolean}
 */
export const isPureWhite = (hex) => {
  const rgb = parseHex(hex);
  return !!rgb && rgb.r === 255 && rgb.g === 255 && rgb.b === 255;
};
